import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogActions, DialogContent, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import ButtonWithIcon from '../ButtonWithIcon/ButtonWithIcon';
import Heading from '../Heading/Heading';
import Paragraph from '../Paragraph/Paragraph';
import CardUploadSlots from '../cardUpload/CardUploadSlots';

const emptySlots = () => [null, null];

/** Only a preview we made ourselves can be released; a stored URL belongs to the backend. */
const revokePreview = (url) => {
    if (typeof url === 'string' && url.startsWith('blob:')) URL.revokeObjectURL(url);
};

const UploadButton = ({ UploadedHCC, formik, storedUrls = [], onRemoveStored }) => {
    const hccImages = formik?.values?.hccImages || [];
    const error = formik?.errors || {};

    const [selectedFiles, setSelectedFiles] = useState(() => [
        hccImages[0] || null,
        hccImages[1] || null,
    ]);
    const [filePreviews, setFilePreviews] = useState(emptySlots);
    const [open, setOpen] = useState(false);
    const dialogTimeoutRef = useRef(null);

    const handleClose = () => {
        setOpen(false);
        if (dialogTimeoutRef.current) {
            clearTimeout(dialogTimeoutRef.current);
            dialogTimeoutRef.current = null;
        }
    };

    const showDialogWithDelay = () => {
        if (dialogTimeoutRef.current) clearTimeout(dialogTimeoutRef.current);
        dialogTimeoutRef.current = setTimeout(() => {
            setOpen(true);
            dialogTimeoutRef.current = null;
        }, 100);
    };

    const handleSelect = (slotIndex, newFile) => {
        const isImage = newFile.type === 'image/png' || newFile.type === 'image/jpeg';
        const isDuplicate = selectedFiles.some(
            (file) => file && file.name === newFile.name && file.size === newFile.size
        );

        if (!isImage || isDuplicate) {
            showDialogWithDelay();
            return;
        }

        revokePreview(filePreviews[slotIndex]);

        const updatedFiles = [...selectedFiles];
        const updatedPreviews = [...filePreviews];
        updatedFiles[slotIndex] = newFile;
        updatedPreviews[slotIndex] = URL.createObjectURL(newFile);

        setSelectedFiles(updatedFiles);
        setFilePreviews(updatedPreviews);
        formik.setFieldValue('hccImages', updatedFiles);
        formik.setFieldError('hccImages', undefined);
        UploadedHCC(newFile);
    };

    const handleRemoveImage = (indexToRemove) => {
        // Nothing chosen this visit means the picture on screen is the one already on file.
        if (!selectedFiles[indexToRemove] && storedUrls[indexToRemove]) {
            onRemoveStored?.(indexToRemove);
            return;
        }

        revokePreview(filePreviews[indexToRemove]);

        const updatedFiles = [...selectedFiles];
        const updatedPreviews = [...filePreviews];
        updatedFiles[indexToRemove] = null;
        updatedPreviews[indexToRemove] = null;

        setFilePreviews(updatedPreviews);
        setSelectedFiles(updatedFiles);
        formik.setFieldValue('hccImages', updatedFiles);
        formik.setFieldError('hccImages', undefined);
    };

    useEffect(() => {
        if (hccImages.filter(Boolean).length === 0 && selectedFiles.some(Boolean)) {
            filePreviews.forEach(revokePreview);
            setSelectedFiles(emptySlots());
            setFilePreviews(emptySlots());
        }
    }, [hccImages]);

    useEffect(() => {
        return () => {
            filePreviews.forEach(revokePreview);
        };
    }, []);

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="invalid-file-title"
                aria-describedby="invalid-file-description"
                sx={{
                    '& .MuiDialog-paper': {
                        backgroundColor: '#eee',
                        padding: '25px',
                        borderRadius: '5px',
                    },
                }}
            >
                <Heading id="invalid-file-title" text={'Invalid File Selection'} />
                <DialogContent id="invalid-file-description" sx={{ padding: '0px' }}>
                    <Paragraph text={'Exactly 2 unique JPG or PNG images are required. Duplicate or unsupported files are not allowed.'} />
                </DialogContent>
                <DialogActions>
                    <ButtonWithIcon variant="outlined" text="Close" startIcon={<CloseIcon />} onClick={handleClose} />
                </DialogActions>
            </Dialog>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px', width: '100%' }}>
                <CardUploadSlots
                    filePreviews={filePreviews.map((preview, index) => preview || storedUrls[index] || null)}
                    placeholderSrc="images/HCCSample.png"
                    placeholderAlt="Health Care Card sample"
                    onSelect={handleSelect}
                    onRemove={handleRemoveImage}
                />

                {error?.hccImages && (
                    <Paragraph color="red" fontSize="13px" text={error.hccImages} />
                )}
            </Box>
        </>
    );
};

export default UploadButton;
